import React, { useEffect, useState } from "react"
import { useValueNullificationOnUnmount } from "source/people/components/hooks/useValueNulificationOnUnmount"
import { useAgeCheck } from "source/people/components/hooks/useAgeCheck"
import PropTypes from "prop-types"
import {
  CheckboxGroup,
  CheckboxProminent,
  Description,
  Field,
  FileInput,
  SectionHeader,
  Select,
  TextArea,
  TextInput,
} from "source/people/components/ui"
import { DateSelector } from "source/shared/components"
import { DatePicker, NumberInput } from "source/shared/components"
import { isEmpty } from "lodash"

const CheckboxesField = ({
  onChange,
  attributes,
  id,
  options,
  otherOption,
  errors,
  value,
  ...props
}) => {
  useValueNullificationOnUnmount({ onChange, id, value })

  const { description } = attributes

  return (
    <Field {...props} id={id} key={id} errors={errors} {...attributes}>
      <Description description={description} />
      <CheckboxGroup
        id={id}
        errors={errors}
        options={options}
        hasOtherOption={!!otherOption}
        onChange={(selected) => onChange(selected, id)}
      />
    </Field>
  )
}

CheckboxesField.propTypes = {
  onChange: PropTypes.func,
  attributes: PropTypes.object,
  id: PropTypes.any,
  options: PropTypes.array,
  otherOption: PropTypes.any,
  errors: PropTypes.any,
  value: PropTypes.any,
}

const DateField = ({
  onChange,
  id,
  attributes,
  errors,
  value,
  personName,
  isBirthdayField = false,
}) => {
  const { description, field_type: fieldType } = attributes
  const { ageInfo, checkAge } = useAgeCheck(isBirthdayField, personName)

  useValueNullificationOnUnmount({ onChange, id, value })

  useEffect(() => {
    if (isBirthdayField) {
      checkAge(value)
    }
  }, [personName, value, isBirthdayField, checkAge])

  const handleChange = (date) => {
    onChange(date, id)
    if (isBirthdayField) checkAge(date)
  }

  return (
    <Field id={id} key={id} errors={errors} {...attributes}>
      <Description description={description} />
      <DateSelector
        id={`${fieldType}_${id}`}
        onChange={handleChange}
        name={fieldType}
        initialValue={value || null}
        className="date-field__input"
      />
      {ageInfo && <div className="mb-1 mt-2p fs-5 c-tint2">{ageInfo}</div>}
    </Field>
  )
}

DateField.propTypes = {
  onChange: PropTypes.func,
  id: PropTypes.any,
  attributes: PropTypes.object,
  errors: PropTypes.any,
  value: PropTypes.any,
  personName: PropTypes.string,
  isBirthdayField: PropTypes.bool,
}

const DatePickerField = ({ onChange, id, attributes, errors, value }) => {
  const { description, field_type: fieldType } = attributes

  useValueNullificationOnUnmount({ onChange, id, value })

  return (
    <Field id={id} key={id} errors={errors} {...attributes}>
      <Description description={description} />
      <DatePicker
        errorClass={!isEmpty(errors) ? "form-field--error" : ""}
        id={`${fieldType}_${id}`}
        onChange={(e) => onChange(e.target.value, id)}
        value={value}
      />
    </Field>
  )
}

DatePickerField.propTypes = {
  onChange: PropTypes.func,
  id: PropTypes.any,
  attributes: PropTypes.object,
  errors: PropTypes.any,
  value: PropTypes.any,
}

const MaskedDateField = ({
  onChange,
  id,
  attributes,
  errors,
  value,
  personName,
  isBirthdayField = false,
}) => {
  const { description, field_type: fieldType } = attributes
  const { ageInfo, checkAge } = useAgeCheck(isBirthdayField, personName)

  useValueNullificationOnUnmount({ onChange, id, value })

  useEffect(() => {
    if (value && isBirthdayField) checkAge(value)
  }, [personName, value, isBirthdayField, checkAge])

  const handleBlur = (e) => {
    const date = e.target.value
    if (isBirthdayField) checkAge(date)
  }

  return (
    <Field id={id} key={id} errors={errors} {...attributes}>
      <Description description={description} />
      <DatePicker
        errorClass={!isEmpty(errors) ? "form-field--error" : ""}
        id={`${fieldType}_${id}`}
        onChange={(e) => onChange(e.target.value, id)}
        onBlur={(e) => handleBlur(e)}
        value={value}
        popperClassName={"d-n"}
      />
      {ageInfo && <div className="mb-1 mt-2p fs-5 c-tint2">{ageInfo}</div>}
    </Field>
  )
}

MaskedDateField.propTypes = {
  onChange: PropTypes.func,
  id: PropTypes.any,
  attributes: PropTypes.object,
  errors: PropTypes.any,
  value: PropTypes.any,
  personName: PropTypes.string,
  isBirthdayField: PropTypes.bool,
}

const DropdownField = ({
  onChange,
  id,
  attributes,
  options,
  otherOption,
  errors,
  value,
  ...props
}) => {
  useValueNullificationOnUnmount({ onChange, id, value })

  const { description, field_type: fieldType } = attributes

  return (
    <Field {...props} id={id} key={id} errors={errors} {...attributes}>
      <Description description={description} />
      <Select
        errors={errors}
        id={`${fieldType}_${id}`}
        hasOtherOption={!!otherOption}
        onChange={(value) => onChange(value, id)}
      >
        {options}
      </Select>
    </Field>
  )
}

DropdownField.propTypes = {
  onChange: PropTypes.func,
  id: PropTypes.any,
  attributes: PropTypes.object,
  options: PropTypes.any,
  otherOption: PropTypes.any,
  errors: PropTypes.any,
  value: PropTypes.any,
}

const FileInputField = ({
  id,
  attributes,
  errors,
  multiFileInput,
  onAddFile,
  onRemoveFile,
  maxTotalFileSize,
  totalFileSizeExceedsLimit,
}) => {
  const [uploadErrors, setUploadErrors] = useState(errors)

  useEffect(() => {
    setUploadErrors(errors)
  }, [errors])

  return (
    <Field id={id} key={id} errors={uploadErrors} {...attributes}>
      <Description description={attributes.description} />
      <FileInput
        key={id}
        id={id}
        blockList={attributes.block_list}
        multiFileInput={multiFileInput}
        onAddFile={onAddFile}
        onRemoveFile={onRemoveFile}
        onUploadErrors={setUploadErrors}
        maxTotalFileSize={maxTotalFileSize}
        totalFileSizeExceedsLimit={totalFileSizeExceedsLimit}
      />
    </Field>
  )
}

FileInputField.propTypes = {
  id: PropTypes.any,
  attributes: PropTypes.object,
  errors: PropTypes.any,
  multiFileInput: PropTypes.any,
  onAddFile: PropTypes.func,
  onRemoveFile: PropTypes.func,
  props: PropTypes.any,
  maxTotalFileSize: PropTypes.any,
  totalFileSizeExceedsLimit: PropTypes.any,
}

const HeadingField = ({ id, attributes, ...props }) => {
  return (
    <Field {...props} id={id} key={id}>
      <SectionHeader key={id} {...attributes} />
    </Field>
  )
}

HeadingField.propTypes = {
  id: PropTypes.any,
  attributes: PropTypes.object,
}

const NumberField = ({ onChange, id, attributes, errors, value, ...props }) => {
  const {
    description,
    settings: { min, max },
  } = attributes

  useValueNullificationOnUnmount({ onChange, id, value })

  const handleChange = (e) => {
    onChange(e.target.value, id)
  }

  // Convert string values to numbers for NumberInput
  const minValue = min !== undefined ? Number(min) : undefined
  const maxValue = max !== undefined ? Number(max) : undefined

  return (
    <Field
      {...props}
      id={id}
      key={id}
      errors={errors}
      help={
        <p className="mb-0">
          {min !== undefined && `min: ${min}`}
          {min !== undefined && max && " / "}
          {max && `max: ${max}`}
        </p>
      }
      {...attributes}
    >
      <Description description={description} />
      <NumberInput
        id={id}
        name={`number_${id}`}
        min={minValue}
        max={maxValue}
        value={value}
        onChange={handleChange}
      />
    </Field>
  )
}

NumberField.propTypes = {
  onChange: PropTypes.func,
  id: PropTypes.any,
  attributes: PropTypes.object,
  errors: PropTypes.any,
  value: PropTypes.any,
}

const TextAreaField = ({
  onChange,
  id,
  attributes,
  errors,
  value,
  ...props
}) => {
  const { placeholder, description, field_type: fieldType } = attributes

  useValueNullificationOnUnmount({ onChange, id, value })

  return (
    <Field {...props} id={id} key={id} errors={errors} {...attributes}>
      <Description description={description} />
      <TextArea
        errors={errors}
        id={`${fieldType}_${id}`}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value, id)}
        maxLength={32000}
      />
    </Field>
  )
}

TextAreaField.propTypes = {
  onChange: PropTypes.func,
  id: PropTypes.any,
  attributes: PropTypes.object,
  errors: PropTypes.any,
  value: PropTypes.any,
}

const TextInputField = ({ onChange, attributes, id, errors, value }) => {
  const { placeholder, description, field_type: fieldType } = attributes

  useValueNullificationOnUnmount({ onChange, id, value })

  return (
    <Field id={id} key={id} errors={errors} {...attributes}>
      <Description description={description} />
      <TextInput
        errors={errors}
        id={`${fieldType}_${id}`}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value, id)}
      />
    </Field>
  )
}

TextInputField.propTypes = {
  onChange: PropTypes.func,
  attributes: PropTypes.object,
  id: PropTypes.any,
  errors: PropTypes.any,
  value: PropTypes.any,
}

const YesNoField = ({ onChange, id, attributes, errors, value, ...props }) => {
  const { description } = attributes

  useValueNullificationOnUnmount({ onChange, id, value })

  return (
    <Field
      {...props}
      id={id}
      key={id}
      errors={errors}
      {...attributes}
      noLabel={true}
    >
      <div>
        <CheckboxProminent
          errors={errors}
          key={id}
          label={attributes.label}
          id={`checkbox_${attributes.label}_${id}`}
          required={attributes.required}
          onChange={(e) => onChange(e.target.checked, id)}
        />
      </div>
      <div className="ml-3">
        <Description description={description} />
      </div>
    </Field>
  )
}

YesNoField.propTypes = {
  onChange: PropTypes.func,
  id: PropTypes.any,
  attributes: PropTypes.object,
  errors: PropTypes.any,
  value: PropTypes.any,
}

export {
  CheckboxesField,
  DateField,
  DatePickerField,
  MaskedDateField,
  DropdownField,
  FileInputField,
  HeadingField,
  NumberField,
  TextAreaField,
  TextInputField,
  YesNoField,
}
